import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Send,
  ArrowLeft,
  Search,
  Plus,
  MessageCircle,
  Check,
  CheckCheck
} from "lucide-react";
import { toast } from "sonner";
import LockedFeature from "../components/LockedFeature";

const Messages = () => {
  const { user, isMemberOrAbove } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isMemberOrAbove()) {
      fetchConversations();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`);
      setConversations(res.data);
    } catch (err) {
      console.error("Conversations fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (partnerId, partnerName) => {
    setLoadingMessages(true);
    setSelectedConversation({ partnerId, partnerName });
    try {
      const res = await axios.get(`${API}/messages/${partnerId}`);
      setMessages(res.data);
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (err) {
      console.error("Messages fetch error:", err);
      toast.error("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages`, {
        recipientId: selectedConversation.partnerId,
        text: newMessage
      });
      setMessages([...messages, res.data]);
      setNewMessage("");
      fetchConversations();
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/members`);
      // Filter out current user
      setAllUsers(res.data.filter(u => u.id !== user.id));
    } catch (err) {
      console.error("Users fetch error:", err);
    }
  };

  const startNewConversation = (targetUser) => {
    setShowNewChat(false);
    setUserSearch("");
    fetchMessages(targetUser.id, targetUser.name);
  };

  const openNewChat = () => {
    setShowNewChat(true);
    fetchAllUsers();
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 60) return `${mins}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
  };

  const filteredUsers = allUsers.filter(u => 
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // If not a member, show locked screen
  if (!isMemberOrAbove()) {
    return (
      <div className="p-5 pb-8 max-w-lg mx-auto" data-testid="messages-locked">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-brand text-[#0B3D2E]">Messages</h1>
          <p className="text-sm text-[#4A5D56]">Private conversations</p>
        </div>
        
        <div className="relative">
          <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl min-h-[300px]">
            <CardContent className="p-8 text-center">
              <MessageCircle className="w-16 h-16 mx-auto text-[#0B3D2E]/20 mb-4" />
              <h3 className="text-lg font-semibold text-[#051F17] mb-2">
                Connect with your whānau
              </h3>
              <p className="text-sm text-[#4A5D56]">
                Send private messages to coaches and other members
              </p>
            </CardContent>
          </Card>
          <LockedFeature title="Private Messages" tier="member" />
        </div>
      </div>
    );
  }

  // Conversation Detail View
  if (selectedConversation) {
    return (
      <div className="flex flex-col h-[calc(100vh-100px)]" data-testid="conversation-view">
        {/* Header */}
        <div className="p-4 border-b border-[#0B3D2E]/10 bg-white flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSelectedConversation(null)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Avatar className="w-10 h-10 bg-[#0B3D2E]">
            <AvatarFallback className="bg-[#0B3D2E] text-white">
              {getInitials(selectedConversation.partnerName)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-[#051F17]">{selectedConversation.partnerName}</p>
            <p className="text-xs text-[#8FA39B]">TRIBE member</p>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {loadingMessages ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 mx-auto text-[#0B3D2E]/20 mb-3" />
              <p className="text-sm text-[#8FA39B]">
                Start the conversation with {selectedConversation.partnerName}
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-w-lg mx-auto">
              {messages.map((msg) => {
                const isMe = msg.senderId === user.id;
                return (
                  <div 
                    key={msg.id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${isMe ? 'order-1' : ''}`}>
                      <div className={`rounded-2xl px-4 py-2 ${
                        isMe 
                          ? 'bg-[#0B3D2E] text-white rounded-br-md' 
                          : 'bg-[#E3EBE5] text-[#051F17] rounded-bl-md'
                      }`}>
                        <p className="text-sm">{msg.text}</p>
                      </div>
                      <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                        <span className="text-[10px] text-[#8FA39B]">
                          {formatTime(msg.createdAt)}
                        </span>
                        {isMe && (
                          msg.read 
                            ? <CheckCheck className="w-3 h-3 text-[#0B3D2E]" />
                            : <Check className="w-3 h-3 text-[#8FA39B]" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t border-[#0B3D2E]/10 bg-white">
          <div className="flex gap-2 max-w-lg mx-auto">
            <Input
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !sending && sendMessage()}
              className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
              data-testid="message-input"
            />
            <Button
              onClick={sendMessage}
              disabled={sending || !newMessage.trim()}
              className="h-12 px-4 bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-xl"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Conversations List View
  return (
    <div className="p-5 pb-8 max-w-lg mx-auto" data-testid="messages-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-brand text-[#0B3D2E]">Messages</h1>
          <p className="text-sm text-[#4A5D56]">Private conversations</p>
        </div>
        <Button 
          onClick={openNewChat}
          className="bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full"
          data-testid="new-chat-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {/* Conversations List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
        </div>
      ) : conversations.length === 0 ? (
        <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl">
          <CardContent className="p-8 text-center">
            <MessageCircle className="w-12 h-12 mx-auto text-[#0B3D2E]/20 mb-4" />
            <h3 className="text-lg font-semibold text-[#051F17] mb-2">
              No conversations yet
            </h3>
            <p className="text-sm text-[#4A5D56] mb-4">
              Start a conversation with another TRIBE member
            </p>
            <Button 
              onClick={openNewChat}
              className="bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-1" /> Start Chat
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv) => (
            <Card 
              key={conv.partnerId}
              className="bg-white border border-[#0B3D2E]/5 rounded-xl cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => fetchMessages(conv.partnerId, conv.partnerName)}
              data-testid={`conversation-${conv.partnerId}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="relative">
                  <Avatar className="w-12 h-12 bg-[#0B3D2E]">
                    <AvatarFallback className="bg-[#0B3D2E] text-white">
                      {getInitials(conv.partnerName)}
                    </AvatarFallback>
                  </Avatar>
                  {conv.unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#B85C4C] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {conv.unreadCount}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-[#051F17]">{conv.partnerName}</p>
                    {conv.lastMessage && (
                      <span className="text-[10px] text-[#8FA39B]">
                        {formatTime(conv.lastMessage.createdAt)}
                      </span>
                    )}
                  </div>
                  {conv.lastMessage && (
                    <p className={`text-sm truncate ${
                      conv.unreadCount > 0 ? 'text-[#051F17] font-medium' : 'text-[#8FA39B]'
                    }`}>
                      {conv.lastMessage.senderId === user.id ? 'You: ' : ''}
                      {conv.lastMessage.text}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Chat Dialog */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-brand">New Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA39B]" />
              <Input
                placeholder="Search members..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="pl-10 h-12 bg-[#F9F9F7] border-0 rounded-xl"
                data-testid="user-search-input"
              />
            </div>
            <ScrollArea className="h-[300px]">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-sm text-[#8FA39B] py-4">
                  No members found
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => startNewConversation(u)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#E3EBE5]/50 transition-colors"
                      data-testid={`user-${u.id}`}
                    >
                      <Avatar className="w-10 h-10 bg-[#0B3D2E]">
                        <AvatarFallback className="bg-[#0B3D2E] text-white text-sm">
                          {getInitials(u.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="font-medium text-[#051F17]">{u.name}</p>
                        <p className="text-xs text-[#8FA39B]">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
